import React, { useState, useEffect } from 'react';
import { Add as AddIcon, Schedule as ScheduleIcon, People as PeopleIcon, CheckCircle as CheckCircleIcon, ViewModule as GridViewIcon, ViewList as ListViewIcon, FilterList as FilterIcon, Search as SearchIcon, LocationOn as LocationIcon, Close as CloseIcon, HelpOutline as HelpIcon, CalendarToday as CalendarIcon, ViewTimeline as TemplateIcon } from '@mui/icons-material';
import { Tooltip, Autocomplete, TextField, Skeleton, FormControlLabel, Checkbox } from '@mui/material';
import { Shift, Driver, Assignment } from '../types';
import { getStatusColor, formatDateToDDMMYY } from '../utils';
import DatePickerDDMMYY from '../../../components/common/DatePickerDDMMYY';
import { ShiftCard } from './ShiftCard';
import { ShiftTableView } from './ShiftTableView';
import CustomButton from '../../../components/common/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { cityOptions } from '../../../constants/enums';
import useAsyncFilterFns from '../../../helper/asyncFilterFns';
import { ShiftTemplateFilter } from './ShiftTemplateFilter';

interface ShiftsViewProps {
  shifts: Shift[];
  drivers: Driver[];
  assignments: Assignment[];
  getAssignedDriversForShift: (shiftId: string) => string[];
  getDriverStatusCounts: (shiftId: string) => {
    online: number;
    busy: number;
    idle: number;
    total: number;
  };
  onToggleShift: (shiftId: string) => void;
  onViewShiftDetails: (shiftId: string) => void;
  onEditShift?: (shiftId: string) => void;
  onCreateShift: () => void;
  isShiftEnabled: (shift: Shift) => boolean;
  viewMode: 'card' | 'table';
  onViewModeChange: (mode: 'card' | 'table') => void;
  paginatedShifts: Shift[];
  page: number;
  totalPages: number;
  totalShifts: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  filters: {
    cityId?: number | undefined;
    neighborhoodId?: number | undefined;
    status?: string | undefined;
    startDate?: string | undefined;
    endDate?: string | undefined;
    search?: string | undefined;
    order?: string | undefined;
    shiftTemplateId?: string | undefined;
    activeNow?: boolean | undefined;
  };
  onFiltersChange: (filters: {
    cityId?: number | undefined;
    neighborhoodId?: number | undefined;
    status?: string | undefined;
    startDate?: string | undefined;
    endDate?: string | undefined;
    search?: string | undefined;
    order?: string | undefined;
    shiftTemplateId?: string | undefined;
    activeNow?: boolean | undefined;
  }) => void;
}

export const ShiftsView: React.FC<ShiftsViewProps> = ({
  shifts,
  drivers,
  assignments,
  getAssignedDriversForShift,
  getDriverStatusCounts,
  onToggleShift,
  onViewShiftDetails,
  onEditShift,
  onCreateShift,
  isShiftEnabled,
  viewMode,
  onViewModeChange,
  paginatedShifts,
  page,
  totalPages,
  totalShifts,
  onPageChange,
  loading = false,
  filters,
  onFiltersChange,
}) => {
  const ITEMS_PER_PAGE = 12;
  const { fetch_neighborhoods } = useAsyncFilterFns();
  const [neighborhoods, setNeighborhoods] = useState<Array<{ id: number; name: string }>>([]);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || '');
  const [templateNameCache, setTemplateNameCache] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const currentSearch = filters.search || '';
    if (debouncedSearch !== currentSearch) {
      onFiltersChange({ ...filters, search: debouncedSearch || undefined });
      onPageChange(0);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (filters.cityId) {
      fetch_neighborhoods({ page: 1 }).then((res) => {
        if (res?.options) {
          const filtered = res.options.filter((n: any) => n.cityId === filters.cityId);
          const sorted = filtered.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
          setNeighborhoods(sorted);
        }
      });
    } else {
      setNeighborhoods([]);
      if (filters.neighborhoodId) {
        onFiltersChange({ ...filters, neighborhoodId: undefined });
      }
    }
  }, [filters.cityId]);

  const displayedShifts = shifts;

  const hasActiveFilters = !!(filters.status || filters.cityId || filters.neighborhoodId || filters.startDate || filters.endDate || filters.search || filters.shiftTemplateId || (filters.order && filters.order !== 'desc'));

  const effectiveTotalShifts = totalShifts;
  const effectiveTotalPages = Math.ceil((totalShifts || 0) / ITEMS_PER_PAGE);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    if (key === 'cityId' && !value) {
      newFilters.neighborhoodId = undefined;
    }
    onFiltersChange(newFilters);
    onPageChange(0);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      cityId: undefined,
      neighborhoodId: undefined,
      status: undefined,
      startDate: undefined,
      endDate: undefined,
      search: undefined,
      order: 'desc',
      shiftTemplateId: undefined,
      activeNow: undefined,
    });
    onPageChange(0);
  };

  return (
    <div className="main-wrapper">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="pageTitle mb-1 text-xl font-bold">Shift Session</h2>
          <p className="text-sm text-gray-500">
            Overview and shift management
          </p>
        </div>
        {/* Temporarily hidden: Create Shift button */}
        {/* <CustomButton
          variant="contained"
          color="primary"
          onClick={onCreateShift}
          additionalStyles="!flex-none text-sm py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 !min-w-0 px-4"
        >
          <div className="flex items-center justify-center gap-2 whitespace-nowrap">
            <AddIcon fontSize="small" />
            <span>Create Shift</span>
          </div>
        </CustomButton> */}
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        {loading ? (
          <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm px-4 py-3 flex items-center gap-3 min-w-[140px]">
            <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '8px' }} />
            <div className="flex-1">
              <Skeleton variant="text" width={80} height={14} />
              <Skeleton variant="text" width={50} height={24} className="mt-1" />
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
              <ScheduleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">Total Shift Sessions</p>
              <p className="text-xl font-bold text-gray-900">{totalShifts}</p>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-gray-900">All Shift Sessions</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage and monitor shift schedules to coordinate driver assignments and operations
            </p>
          </div>
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 shadow-inner" role="group">
            <button
              type="button"
              onClick={() => onViewModeChange('card')}
              className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'card'
                  ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              title="Card View"
              aria-pressed={viewMode === 'card'}
            >
              <GridViewIcon fontSize="small" />
              <span className="hidden sm:inline">Card</span>
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange('table')}
              className={`relative px-5 py-2.5 text-sm font-semibold rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'table'
                  ? 'bg-white text-primaryColor shadow-md transform scale-[1.02]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              title="Table View"
              aria-pressed={viewMode === 'table'}
            >
              <ListViewIcon fontSize="small" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm p-5 mb-6">
          <div className="flex items-center flex-wrap gap-4">
            <div className="flex items-center gap-2 mr-2">
              <FilterIcon sx={{ fontSize: '18px', color: '#6B7280' }} />
              <span className="text-sm font-semibold text-gray-700">Filters:</span>
              <Tooltip
                title="Use filters to narrow down shifts. Active filters appear as removable tags."
                arrow
                placement="top"
              >
                <HelpIcon sx={{ fontSize: '16px', color: '#9CA3AF', cursor: 'help' }} />
              </Tooltip>
            </div>

            {/* Search */}
            <Tooltip title="Search shifts" arrow placement="top">
              <div className="relative">
                <SearchIcon sx={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#9CA3AF' }} />
                <input
                  type="text"
                  value={debouncedSearch}
                  onChange={(e) => setDebouncedSearch(e.target.value)}
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300/80 rounded-[5px] focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor outline-none w-40 bg-white/90 hover:bg-white transition-all shadow-sm hover:shadow"
                />
              </div>
            </Tooltip>

            {/* Status */}
            <Tooltip title="Filter by status" arrow placement="top">
              <Autocomplete
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
                getOptionLabel={(option) => option.label || ''}
                value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
                onChange={(event, newValue) => {
                  handleFilterChange('status', newValue ? newValue.value : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Status"
                    size="small"
                    sx={{
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        borderRadius: '5px',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#D1D5DB',
                          borderRadius: '5px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm">{option.label}</span>
                  </li>
                )}
                noOptionsText="No status found"
              />
            </Tooltip>

            {/* City */}
            <Tooltip title="Filter by city" arrow placement="top">
              <Autocomplete
                options={cityOptions}
                getOptionLabel={(option) => option.name || ''}
                value={cityOptions.find(c => c.id === filters.cityId) || null}
                onChange={(event, newValue) => {
                  handleFilterChange('cityId', newValue ? newValue.id : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Cities"
                    size="small"
                    sx={{
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        borderRadius: '5px',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#D1D5DB',
                          borderRadius: '5px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} className="px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm">{option.name}</span>
                  </li>
                )}
                noOptionsText="No cities found"
              />
            </Tooltip>

            {/* Neighborhood */}
            {filters.cityId && (
              <Tooltip title="Filter by neighborhood" arrow placement="top">
                <Autocomplete
                  options={neighborhoods}
                  getOptionLabel={(option) => option.name || ''}
                  value={neighborhoods.find(n => n.id === filters.neighborhoodId) || null}
                  onChange={(event, newValue) => {
                    handleFilterChange('neighborhoodId', newValue ? newValue.id : undefined);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="All Neighborhoods"
                      size="small"
                      sx={{
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.875rem',
                          padding: '0.5rem',
                          '& fieldset': {
                            borderWidth: '1px',
                            borderColor: '#D1D5DB',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9CA3AF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3B82F6',
                            borderWidth: '1px',
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id} className="px-4 py-2 hover:bg-gray-50">
                      <span className="text-sm">{option.name}</span>
                    </li>
                  )}
                  noOptionsText="No neighborhoods found"
                />
              </Tooltip>
            )}

            {/* Shift Schedule */}
            <ShiftTemplateFilter
              value={filters.shiftTemplateId}
              onChange={(value) => handleFilterChange('shiftTemplateId', value)}
                    placeholder="All Shift Schedules"
              tooltipTitle="Filter by shift schedule"
              minWidth="180px"
              onTemplateNameChange={(templateId, templateName) => {
                if (templateName) {
                  setTemplateNameCache((prev) => ({ ...prev, [templateId]: templateName }));
                }
              }}
            />

            {/* Start Date */}
            <DatePickerDDMMYY
              value={filters.startDate || ''}
              onChange={(value) => handleFilterChange('startDate', value || undefined)}
              placeholder="Start Date: dd/mm/yy"
              tooltip="Filter shifts by start date. Click to open calendar picker."
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  },
                },
              }}
            />

            {/* End Date */}
            <DatePickerDDMMYY
              value={filters.endDate || ''}
              onChange={(value) => handleFilterChange('endDate', value || undefined)}
              placeholder="End Date: dd/mm/yy"
              tooltip="Filter shifts by end date. Click to open calendar picker. Must be after start date."
              minDate={filters.startDate}
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  },
                },
              }}
            />

            {/* Order */}
            <Tooltip title="Sort order" arrow placement="top">
              <Autocomplete
                options={[
                  { value: 'desc', label: 'Newest First' },
                  { value: 'asc', label: 'Oldest First' },
                ]}
                getOptionLabel={(option) => option.label || ''}
                value={filters.order ? { value: filters.order, label: filters.order === 'desc' ? 'Newest First' : 'Oldest First' } : { value: 'desc', label: 'Newest First' }}
                onChange={(event, newValue) => {
                  handleFilterChange('order', newValue ? newValue.value : 'desc');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Sort Order"
                    size="small"
                    sx={{
                      minWidth: 180,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        borderRadius: '5px',
                        '& fieldset': {
                          borderWidth: '1px',
                          borderColor: '#D1D5DB',
                          borderRadius: '5px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9CA3AF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value} className="px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm">{option.label}</span>
                  </li>
                )}
                noOptionsText="No options found"
              />
            </Tooltip>

            {/* Active Now Filter */}
            <Tooltip title="Show only shifts that are currently active (ongoing)" arrow placement="top">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.activeNow === true}
                    onChange={(e) => handleFilterChange('activeNow', e.target.checked ? true : undefined)}
                    size="small"
                    sx={{
                      color: '#3B82F6',
                      '&.Mui-checked': {
                        color: '#3B82F6',
                      },
                    }}
                  />
                }
                label={
                  <span className="text-sm font-medium text-gray-700">
                    Active Now
                  </span>
                }
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Tooltip>

            {/* Active Filter Tags */}
            {filters.status && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '16px' }} />
                <span>{filters.status}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('status', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.cityId && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <LocationIcon sx={{ fontSize: '16px' }} />
                <span>{cityOptions.find(c => c.id === filters.cityId)?.name}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('cityId', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.neighborhoodId && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <LocationIcon sx={{ fontSize: '16px' }} />
                <span>{neighborhoods.find(n => n.id === filters.neighborhoodId)?.name}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('neighborhoodId', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.shiftTemplateId && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <TemplateIcon sx={{ fontSize: '16px' }} />
                <span>
                  {(() => {
                    const name = templateNameCache[filters.shiftTemplateId] || `Shift Schedule ${filters.shiftTemplateId}`;
                    return `${name} (${filters.shiftTemplateId})`;
                  })()}
                </span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('shiftTemplateId', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.startDate && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <CalendarIcon sx={{ fontSize: '16px' }} />
                <span>From: {formatDateToDDMMYY(filters.startDate)}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('startDate', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.endDate && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <CalendarIcon sx={{ fontSize: '16px' }} />
                <span>To: {formatDateToDDMMYY(filters.endDate)}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('endDate', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.search && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <SearchIcon sx={{ fontSize: '16px' }} />
                <span>{filters.search}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('search', undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {filters.order && filters.order !== 'desc' && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primaryColor/10 text-primaryColor rounded-lg text-sm font-medium border border-primaryColor/20 shadow-sm hover:shadow transition-all">
                <ScheduleIcon sx={{ fontSize: '16px' }} />
                <span>Order: {filters.order === 'asc' ? 'Oldest First' : 'Newest First'}</span>
                <Tooltip title="Remove this filter" arrow>
                  <button
                    onClick={() => handleFilterChange('order', 'desc')}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-1 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {/* Clear All */}
            {hasActiveFilters && (
              <Tooltip title="Remove all active filters" arrow placement="top">
                <button
                  onClick={clearAllFilters}
                  className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100/80 rounded-lg transition-all shadow-sm hover:shadow"
                >
                  Clear All
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              <div className="col-span-full flex flex-col items-center justify-center py-20">
                <CircularProgress size={48} sx={{ color: '#3B82F6', mb: 2 }} />
                <p className="text-sm font-medium text-gray-600">Loading</p>
              </div>
            ) : displayedShifts.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
                <ScheduleIcon sx={{ fontSize: '48px', color: '#9CA3AF', marginBottom: '8px' }} />
                <p className="text-sm font-medium text-gray-600">No shifts found</p>
                {/* Temporarily hidden: Create Shift button reference */}
                {/* <p className="text-xs text-gray-400 mt-1">Click "Create Shift" to get started</p> */}
              </div>
            ) : (
              displayedShifts.map((shift) => (
                <ShiftCard
                  key={shift.id}
                  shift={shift}
                  onToggle={onToggleShift}
                  onViewDetails={onViewShiftDetails}
                  onEdit={onEditShift}
                  getStatusColor={getStatusColor}
                  isShiftEnabled={isShiftEnabled}
                />
              ))
            )}
          </div>
        ) : (
          <ShiftTableView
            shifts={displayedShifts}
            onToggle={onToggleShift}
            onViewDetails={onViewShiftDetails}
            onEdit={onEditShift}
            getStatusColor={getStatusColor}
            isShiftEnabled={isShiftEnabled}
            loading={loading}
          />
        )}

        {effectiveTotalShifts > 0 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {page * ITEMS_PER_PAGE + 1} to{' '}
              {Math.min((page + 1) * ITEMS_PER_PAGE, effectiveTotalShifts)} of{' '}
              {effectiveTotalShifts} shift{effectiveTotalShifts !== 1 ? 's' : ''}
              {hasActiveFilters && ' (filtered)'}
            </div>
            {effectiveTotalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === 0
                      ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  Previous
                </button>
                {(() => {
                  const pagesToShow = [];
                  const maxPagesToShow = 5;
                  let startPage = Math.max(0, page - Math.floor(maxPagesToShow / 2));
                  let endPage = Math.min(effectiveTotalPages - 1, startPage + maxPagesToShow - 1);

                  if (endPage - startPage < maxPagesToShow - 1) {
                    startPage = Math.max(0, endPage - maxPagesToShow + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pagesToShow.push(i);
                  }

                  return pagesToShow.map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page === pageNum
                          ? 'bg-primaryColor text-white border border-primaryColor'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => onPageChange(Math.min(effectiveTotalPages - 1, page + 1))}
                  disabled={page >= effectiveTotalPages - 1}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${page >= effectiveTotalPages - 1
                      ? 'bg-white text-gray-400 cursor-not-allowed border border-gray-200'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
